import { Box, Group, createStyles, getStylesRef } from '@mantine/core';
import type { FilterOption } from '@repo/common/components/Filter';
import {
	AddFilter,
	FILTER_OPTIONS_DIVIDER,
	Filter,
	FilterOptionType,
	OPERATORS_CONFIG,
} from '@repo/common/components/Filter';
import { TopLevelOperatorToggle } from '@repo/common/components/Filter/TopLevelOperatorToggle';
import { IconButton } from '@repo/foundations';
import { isNil, noop } from 'lodash-es';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { isValidEnumValue } from '../../../utils/enumUtils';
import type { FilterBlockStore } from '../store';

const useStyles = createStyles((theme) => ({
	ruleBox: {
		borderRadius: theme.radius.md,
		padding: theme.spacing.md,
		width: '100%',
		'&:hover': {
			[`& .${getStylesRef('deleteButton')}`]: {
				opacity: 1,
			},
		},
	},
	filterContainer: {
		flex: 1,
		maxWidth: '100%',
		minWidth: 0,
	},
	deleteButton: {
		ref: getStylesRef('deleteButton'),
		opacity: 0,
	},
}));

interface FilterRuleProps {
	store: FilterBlockStore;
	filterIndex: number;
	readOnly?: boolean;
	hideFilterRemoveButton?: boolean;
}

export const FilterRule = observer(
	({
		store,
		filterIndex,
		readOnly = false,
		hideFilterRemoveButton = false,
	}: FilterRuleProps) => {
		const { classes } = useStyles();

		const filterOptionsMap = useMemo(
			() =>
				store.filterOptions
					.filter((option) => option !== FILTER_OPTIONS_DIVIDER)
					.reduce(
						(acc, option) => {
							acc[option.type] = option;
							return acc;
						},
						{} as Record<FilterOptionType, FilterOption>
					),
			[store.filterOptions]
		);

		const filterRule = store.filterRules[filterIndex];

		return (
			<Box className={classes.ruleBox}>
				<Group position="apart" align="flex-start" noWrap>
					<Group spacing="2xs" className={classes.filterContainer}>
						{filterRule.filtersValues.map((value, idx) => {
							const nestedValue = Array.isArray(value.value)
								? value.value[0]
								: value.value;

							const isValueSet =
								value.isSetApplied ||
								value.isNotSetApplied ||
								!isNil(value.value);

							if (!isValueSet) {
								return null;
							}

							return (
								<Filter
									// eslint-disable-next-line react/no-array-index-key
									key={`filter-${idx}`}
									value={toJS(value)}
									filterOption={toJS(filterOptionsMap[value.filterType])}
									onChange={!readOnly ? filterRule.onChangeValue(idx) : noop}
									onClear={!readOnly ? filterRule.onClearValue(idx) : undefined}
									showDetailedLabel
									readOnly={readOnly}
									operatorConfig={
										OPERATORS_CONFIG[
											filterOptionsMap[value.filterType]?.filterDropdownConfig
												?.dropdownType
										]
									}
									nestedFilterOption={
										isValidEnumValue(FilterOptionType, nestedValue)
											? toJS(filterOptionsMap[nestedValue])
											: undefined
									}
								/>
							);
						})}
						{!readOnly && (
							<AddFilter
								options={toJS(store.filterOptions)}
								onAddFilter={filterRule.onAddValue}
							/>
						)}
					</Group>
					<Group spacing="3xs" noWrap>
						{filterRule.filtersValues.length > 1 && (
							<TopLevelOperatorToggle
								value={filterRule.operator}
								onChange={filterRule.setOperator}
								readOnly={readOnly}
							/>
						)}
						{!hideFilterRemoveButton && !readOnly && (
							<IconButton
								className={classes.deleteButton}
								iconName="x"
								onClick={() => store.removeRule(filterIndex)}
								variant="tertiary"
								tooltip="Remove rule"
							/>
						)}
					</Group>
				</Group>
			</Box>
		);
	}
);
